// Действия руководителя отдела продаж с заведением: правка профиля, назначение
// менеджера и удаление ни разу не платившего. Права проверяет сервер, кнопки
// здесь — только отражение флагов из карточки.

import { useEffect, useMemo, useState } from 'react';
import { Trash2, UserCheck } from 'lucide-react';

import Sheet from '@/components/Sheet';
import { apiDelete, apiErrorMessage, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatBps } from '@/lib/format';
import { ROLE_LABEL } from '@/lib/sales';
import { useAuthStore, type ManagerRole } from '@/store/auth.store';

function FormError({ text }: { text: string | null }) {
  if (!text) return null;
  return <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{text}</div>;
}

// ─── Правка профиля ───────────────────────────────────────────────────────

export interface EditableProfile {
  name: string;
  address: string | null;
  phone: string | null;
  ownerName: string | null;
  ownerPhone: string | null;
  /** Владельца нет — его поля не показываем. */
  hasOwner: boolean;
  /** Точек у владельца: имя и телефон владельца общие для всей сети. */
  networkSize: number;
}

const FIELDS: Array<{ key: keyof Omit<EditableProfile, 'hasOwner' | 'networkSize'>; label: string; owner?: boolean; required?: boolean; type?: string }> = [
  { key: 'name', label: 'Название', required: true },
  { key: 'address', label: 'Адрес' },
  { key: 'phone', label: 'Телефон заведения', type: 'tel' },
  { key: 'ownerName', label: 'Имя владельца', owner: true, required: true },
  { key: 'ownerPhone', label: 'Телефон владельца', owner: true, type: 'tel' },
];

export function EditEstablishmentSheet({
  establishmentId,
  profile,
  onClose,
  onSaved,
}: {
  establishmentId: string;
  profile: EditableProfile;
  onClose: () => void;
  onSaved: (changed: string[]) => void;
}) {
  const [values, setValues] = useState(() => ({
    name: profile.name,
    address: profile.address ?? '',
    phone: profile.phone ?? '',
    ownerName: profile.ownerName ?? '',
    ownerPhone: profile.ownerPhone ?? '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fields = FIELDS.filter((f) => !f.owner || profile.hasOwner);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    // Шлём только изменённое: сервер пишет в журнал «было → стало».
    const body: Record<string, string | null> = {};
    for (const f of fields) {
      const next = values[f.key].trim();
      const prev = (profile[f.key] ?? '').trim();
      if (next === prev) continue;
      if (f.required && !next) return setError(`Поле «${f.label}» не может быть пустым`);
      body[f.key] = next || null;
    }
    if (!Object.keys(body).length) return onClose();
    setBusy(true);
    setError(null);
    try {
      const res = await apiPatch<{ ok: true; changed: string[] }>(`/establishments/${establishmentId}`, body, {
        silent: true,
      });
      onSaved(res.changed);
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось сохранить'));
      setBusy(false);
    }
  };

  return (
    <Sheet title="Профиль заведения" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
        {fields.map((f) => (
          <div key={f.key}>
            <label className="label" htmlFor={`est-${f.key}`}>
              {f.label}
            </label>
            <input
              id={`est-${f.key}`}
              className="input"
              type={f.type ?? 'text'}
              inputMode={f.type === 'tel' ? 'tel' : undefined}
              maxLength={f.key === 'address' ? 200 : f.type === 'tel' ? 32 : 120}
              value={values[f.key]}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            />
            {f.key === 'ownerName' && profile.networkSize > 1 ? (
              <p className="mt-1 text-xs text-amber-700">
                Владелец общий для {profile.networkSize} заведений сети — имя и телефон поменяются во всех.
              </p>
            ) : null}
          </div>
        ))}
        <p className="text-xs text-ink/45">
          Email владельца — его вход в админку всей сети, меняет администратор Resstro. Правка попадёт в журнал.
        </p>
        <FormError text={error} />
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </form>
    </Sheet>
  );
}

// ─── Назначить менеджера ──────────────────────────────────────────────────

interface TeamOption {
  id: string;
  name: string;
  code: string;
  role: ManagerRole;
  status: 'active' | 'blocked';
  commissionBps: number;
  establishments: number;
}

const NOBODY = '__nobody__';

export function AssignManagerSheet({
  establishmentId,
  current,
  onClose,
  onDone,
}: {
  establishmentId: string;
  current: { id: string; name: string } | null;
  onClose: () => void;
  onDone: (referrer: { id: string; name: string; code: string } | null) => void;
}) {
  const myId = useAuthStore((s) => s.manager?.id);
  const [team, setTeam] = useState<TeamOption[] | null>(null);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void apiGet<TeamOption[]>('/managers', { silent: true })
      .then((rows) => {
        if (!cancelled) setTeam(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setTeam([]);
          setError(apiErrorMessage(err, 'Не удалось загрузить команду'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Активные сотрудники, кроме текущего; себя — первым.
  const options = useMemo(
    () =>
      (team ?? [])
        .filter((t) => t.status === 'active' && t.id !== current?.id)
        .sort((a, b) => (a.id === myId ? -1 : b.id === myId ? 1 : a.name.localeCompare(b.name, 'ru'))),
    [team, current?.id, myId],
  );
  const picked = options.find((t) => t.id === choice) ?? null;

  const submit = async () => {
    if (!choice || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost<{ ok: true; referrer: { id: string; name: string; code: string } | null }>(
        `/establishments/${establishmentId}/referrer`,
        { managerId: choice === NOBODY ? null : choice },
        { silent: true },
      );
      onDone(res.referrer);
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось назначить менеджера'));
      setBusy(false);
    }
  };

  return (
    <Sheet title={current ? 'Сменить менеджера' : 'Назначить менеджера'} onClose={onClose}>
      <div className="space-y-4 text-sm">
        {current ? (
          <div className="rounded-lg border border-line px-3 py-2.5">
            <div className="text-xs text-ink/50">Сейчас</div>
            <div className="font-medium">{current.name}</div>
          </div>
        ) : (
          <p className="text-ink/65">
            Клиент пришёл не по реферальной ссылке — привяжите его к менеджеру, который подключал.
          </p>
        )}

        <div>
          <label className="label" htmlFor="assign-manager">
            Менеджер
          </label>
          <select
            id="assign-manager"
            className="input"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            disabled={team === null}
          >
            <option value="" disabled>
              {team === null ? 'Загружаем команду…' : 'Выберите'}
            </option>
            {options.map((t) => (
              <option key={t.id} value={t.id}>
                {t.id === myId ? `Себе (${t.name})` : `${t.name} · ${ROLE_LABEL[t.role]}`} · ставка {formatBps(t.commissionBps)}
              </option>
            ))}
            {current ? <option value={NOBODY}>Снять привязку — без менеджера</option> : null}
          </select>
        </div>

        <ul className="space-y-1.5 text-ink/65">
          {choice === NOBODY ? (
            <li>Комиссию с будущих оплат не получит никто, пока заведение снова не привяжут.</li>
          ) : (
            <li>
              Комиссия — только с будущих оплат
              {picked ? `, по ставке ${formatBps(picked.commissionBps)}` : ''}. Уже прошедшие оплаты не начисляются.
            </li>
          )}
          {current ? <li>Прошлые начисления остаются у {current.name}.</li> : null}
        </ul>

        <FormError text={error} />

        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" className="btn border border-line bg-white text-ink hover:bg-paper" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn-primary" disabled={!choice || busy} onClick={() => void submit()}>
            <UserCheck size={16} /> {busy ? 'Сохраняем…' : choice === NOBODY ? 'Снять' : 'Назначить'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ─── Удаление ─────────────────────────────────────────────────────────────

export interface DeleteUsage {
  orders: number;
  staff: number;
  devices: number;
  ownerKept: boolean;
}

function plural(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru');

export function DeleteEstablishmentSheet({
  establishmentId,
  name,
  usage,
  onClose,
  onDeleted,
}: {
  establishmentId: string;
  name: string;
  usage: DeleteUsage;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = normalize(typed) === normalize(name);

  const submit = async () => {
    if (!matches || busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiDelete(`/establishments/${establishmentId}`, { silent: true, body: { confirmName: typed } });
      onDeleted();
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось удалить заведение'));
      setBusy(false);
    }
  };

  return (
    <Sheet title="Удалить заведение" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-rose-800">
          Удаление необратимо: «{name}» пропадёт вместе с меню, складом, заказами, сменами и кассами.
        </div>
        <ul className="space-y-1.5 text-ink/65">
          <li>
            Заказов: <b className="font-medium text-ink">{usage.orders}</b>, сотрудников:{' '}
            <b className="font-medium text-ink">{usage.staff}</b>, касс и устройств:{' '}
            <b className="font-medium text-ink">{usage.devices}</b>.
          </li>
          <li>
            {usage.ownerKept
              ? 'У владельца есть другие заведения — его вход останется, удалятся только сотрудники этой точки.'
              : 'Вход владельца и сотрудников закроется.'}
          </li>
          <li>Оплат подписки не было — оборот отдела, сейф и комиссии не изменятся.</li>
        </ul>
        <div>
          <label className="label" htmlFor="delete-confirm">
            Введите название заведения, чтобы подтвердить
          </label>
          <input
            id="delete-confirm"
            className="input"
            autoComplete="off"
            placeholder={name}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
        <FormError text={error} />
        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" className="btn border border-line bg-white text-ink hover:bg-paper" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            disabled={!matches || busy}
            onClick={() => void submit()}
          >
            <Trash2 size={16} /> {busy ? 'Удаляем…' : 'Удалить навсегда'}
          </button>
        </div>
        <p className="text-xs text-ink/45">
          {usage.orders} {plural(usage.orders, ['заказ', 'заказа', 'заказов'])} и вся история точки удалятся из базы. В журнале останется запись, кто и когда удалил.
        </p>
      </div>
    </Sheet>
  );
}
